/** Show complete conversation billing with explicit loading, pending and failure states. */
import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '../../shared/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../shared/ui/popover';
import { SITopupControls } from './SITopupControls';
import { formatSICost, loadChatUsage, loadSIUsage, type SIChatUsageScope, type SIChatUsageSummary, type SIUsageSummary } from './siUsage';

type LoadState<T> = { key: string; status: 'loading' } | { key: string; status: 'error'; message: string } | { key: string; status: 'ready'; value: T };
type ContextUsage = { textTokens: number; maxTokens: number; visionReserve: number; compactedResults: number };
const emptyChat: SIChatUsageSummary = {
 inputTokens: 0, outputTokens: 0, totalTokens: 0, knownTotalTokens: 0, unknownTokenRequests: 0, actualHostingCostGbp: 0, actualUsagePriceGbp: 0,
 grossMarginPercentage: 30, costStatus: 'no_usage', pendingRequests: 0, requests: 0,
 costUpdatedAt: null, allocationMethod: 'billed_instance_time_share',
};
const balanceFormat = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

function UsageRow({ label, children }: { label: string; children: ReactNode }) {
 return <div className="flex justify-between gap-4"><span className="text-left text-muted-foreground">{label}</span><span className="text-right font-medium tabular-nums">{children}</span></div>;
}

export function SIUsagePopover({ workspaceId, scope, refreshKey, contextUsage }: {
 workspaceId: string | null | undefined; scope: SIChatUsageScope | null; refreshKey: number; contextUsage: ContextUsage | null;
}) {
 const [open, setOpen] = useState(false);
 const [retry, setRetry] = useState(0);
 const scopeKey = JSON.stringify(scope);
 const walletKey = workspaceId ?? '';
 const [chatState, setChatState] = useState<LoadState<SIChatUsageSummary>>({ key: scopeKey, status: 'loading' });
 const [walletState, setWalletState] = useState<LoadState<SIUsageSummary>>({ key: walletKey, status: 'loading' });
 const scopeProjectId = scope?.projectId;
 const scopeWorkspaceId = scope?.workspaceId;
 const conversationId = scope?.conversationId;
 const chatAvailable = Boolean(scope || workspaceId);
 useEffect(() => {
  if (!open) return;
  const controller = new AbortController();
  setChatState({ key: scopeKey, status: 'loading' });
  if (!chatAvailable) setChatState({ key: scopeKey, status: 'error', message: 'Chat workspace is unavailable.' });
  else if (!conversationId) setChatState({ key: scopeKey, status: 'ready', value: emptyChat });
  else {
   const selected: SIChatUsageScope = scopeProjectId ? { projectId: scopeProjectId, conversationId }
    : { workspaceId: scopeWorkspaceId!, conversationId };
   void loadChatUsage(selected, controller.signal).then(value => {
    if (!controller.signal.aborted) setChatState({ key: scopeKey, status: 'ready', value });
   }).catch(() => {
    if (!controller.signal.aborted) setChatState({ key: scopeKey, status: 'error', message: 'Chat usage could not be loaded.' });
   });
  }
  return () => controller.abort();
 }, [open, scopeKey, chatAvailable, scopeProjectId, scopeWorkspaceId, conversationId, refreshKey, retry]);
 useEffect(() => {
  if (!open) return;
  const controller = new AbortController();
  setWalletState({ key: walletKey, status: 'loading' });
  if (!workspaceId) setWalletState({ key: walletKey, status: 'error', message: 'Workspace balance is unavailable.' });
  else void loadSIUsage(workspaceId, controller.signal).then(value => {
   if (!controller.signal.aborted) setWalletState({ key: walletKey, status: 'ready', value });
  }).catch(() => {
   if (!controller.signal.aborted) setWalletState({ key: walletKey, status: 'error', message: 'Credit balance could not be loaded.' });
  });
  return () => controller.abort();
 }, [open, workspaceId, walletKey, refreshKey, retry]);
 const chat = chatState.key === scopeKey && chatState.status === 'ready' ? chatState.value : null;
 const wallet = walletState.key === walletKey && walletState.status === 'ready' ? walletState.value : null;
 const chatError = chatState.key === scopeKey && chatState.status === 'error' ? chatState.message : null;
 const walletError = walletState.key === walletKey && walletState.status === 'error' ? walletState.message : null;
 const hasCostBasis = chat?.costBasis !== undefined;
 const pendingCost = hasCostBasis ? chat?.costBasis === 'unavailable' : chat?.costStatus === 'pending';
 const usagePrice = hasCostBasis ? chat?.usagePriceGbp : chat?.actualUsagePriceGbp;
 const incompleteTokens = chat !== null && chat.totalTokens === null;
 const tokens = chatError ? 'Unavailable' : !chat ? 'Loading...' : chat.totalTokens != null ? chat.totalTokens.toLocaleString('en-GB')
  : chat.knownTotalTokens > 0 ? chat.knownTotalTokens.toLocaleString('en-GB') : 'Unavailable';
 const cost = (value: number | null | undefined) => chatError ? 'Unavailable' : !chat ? 'Loading...' : pendingCost ? 'Billing updating' : formatSICost(value);
 const refresh = () => setRetry(value => value + 1);
 return <Popover open={open} onOpenChange={setOpen}>
  <PopoverTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 self-end rounded-full text-muted-foreground" aria-label="QuantisAI usage and balance" title="Usage and balance">
   <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="3" opacity="0.3" /><path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
  </Button></PopoverTrigger>
  <PopoverContent align="end" className="w-80 p-0"><div className="max-h-96 overflow-y-auto">
   <p className="px-4 pt-4 text-sm font-semibold">Chat usage &amp; balance</p>
   <section aria-label="QuantisAI usage" className="space-y-3 px-4 py-4 text-sm">
    {contextUsage && <div className="space-y-1">
     <UsageRow label="Context window">{Math.min(100, Math.round((contextUsage.textTokens + contextUsage.visionReserve) / contextUsage.maxTokens * 100))}% reserved</UsageRow>
     <p className="text-xs text-muted-foreground">{contextUsage.textTokens.toLocaleString('en-GB')} text tokens / {contextUsage.maxTokens.toLocaleString('en-GB')} capacity. Includes room reserved for the drawing image. Repeated results are compacted automatically.</p>
    </div>}
    <UsageRow label={incompleteTokens ? 'Tokens recorded' : 'Tokens used'}>{tokens}</UsageRow>
    <UsageRow label="Usage cost">{cost(usagePrice)}</UsageRow>
    {chatError && <div><p role="alert" className="text-xs text-destructive">{chatError}</p><Button variant="outline" size="sm" className="mt-2" onClick={refresh} aria-label="Retry chat usage">Retry</Button></div>}
    <UsageRow label="Credit balance">{walletError ? 'Unavailable' : !wallet ? 'Loading...' : wallet.balanceGbp == null ? 'Not set up' : balanceFormat.format(wallet.balanceGbp)}</UsageRow>
    {walletError && <div><p role="alert" className="text-xs text-destructive">{walletError}</p><Button variant="outline" size="sm" className="mt-2" onClick={refresh} aria-label="Retry credit balance">Retry</Button></div>}
    {wallet?.chargingEnabled && <p className="rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground">Usage is deducted from your credit balance.</p>}
   </section>
   <SITopupControls hideUnavailable workspaceId={workspaceId} onBalanceUpdated={refresh} />
  </div></PopoverContent>
 </Popover>;
}
